import { bunja } from 'bunja';
import { atom } from 'jotai';

import Num from '@flipster/utils/num';

import { mapTable } from '..';
import type { PrexDecimal } from '../../../model/prex.shared.primitives';
import type { PswapOrderSide } from '../../../model/prex.v2.r230522.order';
import type { PswapOrder } from '../../../model/prex.v2.r230522.tables.private_orders';
import type { Position } from '../../../model/prex.v2.r230522.tables.private_positions';
import { Position_fromJsonValue } from '../../../model/prex.v2.r230522.tables.private_positions';
import { marketsBunja } from '../../../original/market';
import { getTickerDisplayPriceOrDefault } from '../../../original/ticker-price';
import { r230522NormalSubSocketBunja } from '../../socket';
import { SubAccountIdScope } from '../../sub-account';
import { accountPositionsBunja } from './account-positions';
import { ordersBunja } from './orders';

export interface OpenPosition extends Position {
  side: PswapOrderSide;
  calcUnrealizedPnlPercent: PrexDecimal;
  calcNetPnlPercent: PrexDecimal;
  fundsInvested: PrexDecimal;
  quantity: PrexDecimal;
  size: PrexDecimal;
  // TODO: remove this; only for legacy compatibility. New code should get markPrice from marketsBunja.
  markPrice: PrexDecimal;
  // tpsl
  tpOrders: PswapOrder[];
  slOrders: PswapOrder[];
  takeProfit: PrexDecimal;
  stopLoss: PrexDecimal;
  tpOrderQty?: PrexDecimal;
  slOrderQty?: PrexDecimal;
}

const zero: PrexDecimal = Num(0);

const basePositionsBunja = bunja(() => {
  const { subscribe, tablesAtom } = bunja.use(r230522NormalSubSocketBunja);

  const positionTableAtom = atom((get) => {
    const tables = get(tablesAtom);
    const positions = tables['private/positions'] || {};
    return mapTable(positions, Position_fromJsonValue);
  });

  const positionsAtom = atom((get) => {
    const positionTable = get(positionTableAtom);
    return Object.entries(positionTable)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([, value]) => value);
  });

  bunja.effect(() => subscribe('private/positions', { rows: ['*'] }));

  return { positionsAtom };
});

export const positionsBunja = bunja(() => {
  const accountId = bunja.use(SubAccountIdScope);
  const base = bunja.will(basePositionsBunja);
  const account = bunja.will(accountPositionsBunja);
  const { positionsAtom } = accountId != null ? account() : base();
  return { positionsAtom };
});

export const openPositionsBunja = bunja(() => {
  const { positionsAtom } = bunja.use(positionsBunja);
  const { ordersAtom } = bunja.use(ordersBunja);
  const { marketsAtom } = bunja.use(marketsBunja);

  const openPositionsAtom = atom((get) => {
    const positions = get(positionsAtom);
    const orders = get(ordersAtom);
    const markets = get(marketsAtom);

    const result: Record<string, OpenPosition[]> = {};

    for (const p of positions) {
      if (p.position.isZero()) continue;

      // 서버에서 음수로 내려 올수도 있음 유저에게는 음수로 보여주지 않도록 함
      let liquidationPrice = p.liquidationPrice;
      if (liquidationPrice && liquidationPrice.isLessThan(0)) liquidationPrice = undefined;

      const side: PswapOrderSide = p.position.isGreaterThanOrEqualTo(0) ? 'Long' : 'Short';
      // for legacy compatibility; new code should get markPrice from marketsBunja.
      const markPrice = Num(markets?.[p.symbol]?.markPrice ?? '0');
      const { tpOrders, slOrders } = findTpSlOrders(orders, p.symbol, p.slot);

      const tpOrder = tpOrders.length > 0 ? tpOrders[0] : undefined;
      const slOrder = slOrders.length > 0 ? slOrders[0] : undefined;

      const takeProfit = tpOrder?.triggerPrice ?? zero;
      const stopLoss = slOrder?.triggerPrice ?? zero;
      const tpOrderQty = tpOrder?.qty;
      const slOrderQty = slOrder?.qty;

      const referencePrice = Num(getTickerDisplayPriceOrDefault(markets?.[p.symbol], { midPrice: String(p.midPrice) }));

      const openPosition: OpenPosition = {
        ...p,
        liquidationPrice,
        side,
        fundsInvested: calcFundsInvested(p),
        quantity: p.position,
        size: p.position.multipliedBy(referencePrice),
        markPrice,
        calcUnrealizedPnlPercent: calcPnlPercent(p.unrealizedPnl, p.marginType, p),
        calcNetPnlPercent: calcPnlPercent(p.netPnl, p.marginType, p),
        takeProfit,
        stopLoss,
        tpOrderQty,
        slOrderQty,
        tpOrders,
        slOrders,
      };

      result[p.symbol] ??= [];
      result[p.symbol].push(openPosition);
    }

    for (const positions of Object.values(result)) {
      positions.sort((a, b) => b.slot - a.slot);
    }

    return result;
  });
  openPositionsAtom.debugLabel = 'openPositionsAtom';

  // positionId로 단건 조회하기 위한 파생 맵. 소켓 tick마다 재생성되지만, 소비 측(카드-스코프 bunja)에서
  // 필드별 atom이 안정 필드는 Object.is로 dedup하므로 이 맵의 identity 변화는 전파를 만들지 않는다.
  const openPositionByIdAtom = atom((get) => {
    const bySymbol = get(openPositionsAtom);
    const result: Record<string, OpenPosition> = {};
    for (const list of Object.values(bySymbol)) {
      for (const position of list) result[position.positionId] = position;
    }
    return result;
  });
  openPositionByIdAtom.debugLabel = 'openPositionByIdAtom';

  const topOpenPositionsAtom = atom((get) => {
    const openPositions = get(openPositionsAtom);
    if (Object.keys(openPositions).length < 1) return undefined;
    return Object.values(openPositions)
      .map((v) => {
        let amount = zero;
        let symbol = '';
        for (const cur of v) {
          symbol = cur.symbol;
          amount = cur.size.abs().plus(amount);
        }
        return { symbol, amount };
      })
      .sort((a, b) => (b.amount.isGreaterThan(a.amount) ? 1 : -1))
      .map((v) => v.symbol)
      .slice(0, 3);
  });
  topOpenPositionsAtom.debugLabel = 'topOpenPositionsAtom';

  const openSymbolsAtom = atom((get) => {
    const openPositions = get(openPositionsAtom);
    if (Object.keys(openPositions).length < 1) return [];
    return Object.keys(openPositions)
      .filter((symbol) => openPositions[symbol].length > 0)
      .sort((a, b) => {
        const bLast = openPositions[b].at(-1);
        const aLast = openPositions[a].at(-1);
        if (!bLast || !aLast) return 0;
        return bLast.openTime.getTime() - aLast.openTime.getTime();
      });
  });
  openSymbolsAtom.debugLabel = 'openSymbolsAtom';

  return { openPositionsAtom, openPositionByIdAtom, topOpenPositionsAtom, openSymbolsAtom };
});
openPositionsBunja.debugLabel = 'openPositionsBunja';

function calcPnlPercent(pnl: PrexDecimal, marginType: Position['marginType'], position: Position): PrexDecimal {
  switch (marginType) {
    case 'Cross': {
      if (position.initPositionMargin.isZero()) return zero;
      return pnl.dividedBy(position.initPositionMargin).multipliedBy(100);
    }
    case 'Isolated': {
      const margin = position.isolatedMarginInitial.plus(position.isolatedMarginAdded);
      if (margin.isZero()) return zero;
      return pnl.dividedBy(margin).multipliedBy(100);
    }
    default:
      return zero;
  }
}

function calcFundsInvested(position: Position): PrexDecimal {
  switch (position.marginType) {
    case 'Cross':
      return position.initPositionMargin;
    case 'Isolated':
      return position.isolatedMarginInitial.plus(position.isolatedMarginAdded);
    default:
      return zero;
  }
}

function findTpSlOrders(orders: PswapOrder[], symbol: string, slot: number) {
  const openPositionTypes: PswapOrder['conditionalOrderType'][] = [
    'CONDITIONAL_ORDER_TYPE_POSITION_TP',
    'CONDITIONAL_ORDER_TYPE_POSITION_SL',
  ];
  const filteredTpSl = orders.filter(
    (o) => o.symbol === symbol && o.slot === slot && openPositionTypes.includes(o.conditionalOrderType),
  );

  // (OrderSide::Long, TriggerDirection::Down) | (OrderSide::Short, TriggerDirection::Up) => TP
  const tpOrders = (filteredTpSl || []).filter(
    (v) => (v.side === 'Long' && v.triggerDirection === 'Down') || (v.side === 'Short' && v.triggerDirection === 'Up'),
  );

  // (OrderSide::Long, TriggerDirection::Up) | (OrderSide::Short, TriggerDirection::Down) => SL
  const slOrders = (filteredTpSl || []).filter(
    (v) => (v.side === 'Long' && v.triggerDirection === 'Up') || (v.side === 'Short' && v.triggerDirection === 'Down'),
  );

  return { tpOrders, slOrders };
}
